import { useQuery } from '@tanstack/react-query';
import { fetchMentorshipProgramsList } from '@/features/mentorship/api/mentorship.api';
import {
  mapListRowToView,
  type MentorshipListProgramView,
} from '@/features/mentorship/lib/map-mentorship-views';
import type { MentorshipProgramListQuery } from '@/shared/job-mentorship';

const defaultListQuery: Partial<MentorshipProgramListQuery> = {
  active: 1,
  limit: 100,
};

export const mentorshipQueryKeys = {
  all: ['mentorship'] as const,
  programs: () => [...mentorshipQueryKeys.all, 'programs'] as const,
  programList: (
    query: Partial<MentorshipProgramListQuery>,
    excludeExpired: boolean
  ) =>
    [...mentorshipQueryKeys.programs(), 'list', query, excludeExpired] as const,
};

function isProgramEndOpen(endOn: number, nowSeconds: number): boolean {
  return endOn <= 0 || endOn > nowSeconds;
}

export function useMentorshipProgramsList(
  query: Partial<MentorshipProgramListQuery> = defaultListQuery,
  options?: { excludeExpired?: boolean }
) {
  const excludeExpired = options?.excludeExpired ?? false;

  return useQuery({
    queryKey: mentorshipQueryKeys.programList(query, excludeExpired),
    queryFn: async (): Promise<MentorshipListProgramView[]> => {
      const res = await fetchMentorshipProgramsList(query);
      const nowSeconds = Math.floor(Date.now() / 1000);
      const rows = excludeExpired
        ? res.data.filter((row) => isProgramEndOpen(row.endOn, nowSeconds))
        : res.data;
      return rows.map(mapListRowToView);
    },
  });
}
